/**
 * Domain enumerations.
 *
 * Declared as frozen const objects rather than TypeScript `enum`s so the values
 * exist at runtime in the browser bundle (for labels, filters and badge maps)
 * while staying structurally identical to the Prisma enums of the same name.
 */
export const UserStatus = {
    PENDING_VERIFICATION: 'PENDING_VERIFICATION',
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    SUSPENDED: 'SUSPENDED',
};
export const MemberStatus = {
    PENDING: 'PENDING',
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    SUSPENDED: 'SUSPENDED',
    EXITED: 'EXITED',
};
export const KycStatus = {
    NOT_STARTED: 'NOT_STARTED',
    IN_PROGRESS: 'IN_PROGRESS',
    SUBMITTED: 'SUBMITTED',
    UNDER_REVIEW: 'UNDER_REVIEW',
    REQUIRES_CORRECTION: 'REQUIRES_CORRECTION',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
export const DocumentType = {
    GOVERNMENT_ID: 'GOVERNMENT_ID',
    PASSPORT_PHOTOGRAPH: 'PASSPORT_PHOTOGRAPH',
    PROOF_OF_ADDRESS: 'PROOF_OF_ADDRESS',
    EMPLOYMENT_DOCUMENT: 'EMPLOYMENT_DOCUMENT',
    MEMBERSHIP_EVIDENCE: 'MEMBERSHIP_EVIDENCE',
    BANK_STATEMENT: 'BANK_STATEMENT',
    SIGNATURE: 'SIGNATURE',
    OTHER: 'OTHER',
};
export const DocumentStatus = {
    UPLOADED: 'UPLOADED',
    VERIFIED: 'VERIFIED',
    REJECTED: 'REJECTED',
    REPLACED: 'REPLACED',
};
/**
 * The pre-approval lifecycle. An application that never reaches APPROVED never
 * produces a `Loan` row, which keeps the financial ledger free of rejected
 * requests.
 */
export const LoanApplicationStatus = {
    DRAFT: 'DRAFT',
    SUBMITTED: 'SUBMITTED',
    UNDER_REVIEW: 'UNDER_REVIEW',
    MORE_INFORMATION_REQUIRED: 'MORE_INFORMATION_REQUIRED',
    AWAITING_GUARANTORS: 'AWAITING_GUARANTORS',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    WITHDRAWN: 'WITHDRAWN',
};
/** Terminal application states — no further transition is permitted. */
export const TERMINAL_APPLICATION_STATUSES = [
    LoanApplicationStatus.APPROVED,
    LoanApplicationStatus.REJECTED,
    LoanApplicationStatus.WITHDRAWN,
];
/**
 * Permitted application transitions. The service layer refuses anything not
 * listed here, so an invalid state change is impossible rather than merely
 * discouraged.
 */
export const LOAN_APPLICATION_TRANSITIONS = {
    DRAFT: [LoanApplicationStatus.SUBMITTED, LoanApplicationStatus.WITHDRAWN],
    SUBMITTED: [
        LoanApplicationStatus.UNDER_REVIEW,
        LoanApplicationStatus.REJECTED,
        LoanApplicationStatus.WITHDRAWN,
    ],
    UNDER_REVIEW: [
        LoanApplicationStatus.MORE_INFORMATION_REQUIRED,
        LoanApplicationStatus.AWAITING_GUARANTORS,
        LoanApplicationStatus.APPROVED,
        LoanApplicationStatus.REJECTED,
    ],
    MORE_INFORMATION_REQUIRED: [
        LoanApplicationStatus.SUBMITTED,
        LoanApplicationStatus.WITHDRAWN,
        LoanApplicationStatus.REJECTED,
    ],
    AWAITING_GUARANTORS: [
        LoanApplicationStatus.UNDER_REVIEW,
        LoanApplicationStatus.APPROVED,
        LoanApplicationStatus.REJECTED,
        LoanApplicationStatus.WITHDRAWN,
    ],
    APPROVED: [],
    REJECTED: [],
    WITHDRAWN: [],
};
/** The post-approval ledger lifecycle. */
export const LoanStatus = {
    PENDING_DISBURSEMENT: 'PENDING_DISBURSEMENT',
    DISBURSED: 'DISBURSED',
    ACTIVE: 'ACTIVE',
    COMPLETED: 'COMPLETED',
    DEFAULTED: 'DEFAULTED',
    WRITTEN_OFF: 'WRITTEN_OFF',
    CANCELLED: 'CANCELLED',
};
export const LOAN_TRANSITIONS = {
    PENDING_DISBURSEMENT: [LoanStatus.DISBURSED, LoanStatus.CANCELLED],
    DISBURSED: [LoanStatus.ACTIVE, LoanStatus.CANCELLED],
    ACTIVE: [LoanStatus.COMPLETED, LoanStatus.DEFAULTED],
    DEFAULTED: [LoanStatus.ACTIVE, LoanStatus.COMPLETED, LoanStatus.WRITTEN_OFF],
    COMPLETED: [],
    WRITTEN_OFF: [],
    CANCELLED: [],
};
/** Statuses that count as money currently out with members. */
export const OUTSTANDING_LOAN_STATUSES = [
    LoanStatus.DISBURSED,
    LoanStatus.ACTIVE,
    LoanStatus.DEFAULTED,
];
export const RepaymentFrequency = {
    WEEKLY: 'WEEKLY',
    BIWEEKLY: 'BIWEEKLY',
    MONTHLY: 'MONTHLY',
    QUARTERLY: 'QUARTERLY',
};
export const InstallmentStatus = {
    PENDING: 'PENDING',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    PAID: 'PAID',
    OVERDUE: 'OVERDUE',
    WAIVED: 'WAIVED',
};
/**
 * Where a guarantor stands.
 *
 * A guarantor is not a user of this system. They are someone the borrower has
 * already spoken to, whom a UNIFEMGA officer then telephones to confirm. These
 * statuses describe that call — there is no in-app accept or decline, because
 * most guarantors will never have an account.
 */
export const GuarantorStatus = {
    PENDING_CONFIRMATION: 'PENDING_CONFIRMATION',
    CONFIRMED: 'CONFIRMED',
    UNREACHABLE: 'UNREACHABLE',
    DECLINED: 'DECLINED',
    WITHDRAWN: 'WITHDRAWN',
    RELEASED: 'RELEASED',
};
export const DisbursementStatus = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    PROCESSING: 'PROCESSING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
};
export const PaymentMethod = {
    BANK_TRANSFER: 'BANK_TRANSFER',
    DIRECT_DEBIT: 'DIRECT_DEBIT',
    SALARY_DEDUCTION: 'SALARY_DEDUCTION',
    CASH: 'CASH',
    CHEQUE: 'CHEQUE',
    ONLINE: 'ONLINE',
    OTHER: 'OTHER',
};
export const RepaymentStatus = {
    PENDING: 'PENDING',
    CONFIRMED: 'CONFIRMED',
    REVERSED: 'REVERSED',
    FAILED: 'FAILED',
};
export const NotificationChannel = {
    EMAIL: 'EMAIL',
    SMS: 'SMS',
    WHATSAPP: 'WHATSAPP',
    IN_APP: 'IN_APP',
};
export const NotificationStatus = {
    SCHEDULED: 'SCHEDULED',
    QUEUED: 'QUEUED',
    SENT: 'SENT',
    DELIVERED: 'DELIVERED',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED',
};
/** Domain events that the notification service listens for. */
export const NotificationEvent = {
    MEMBER_REGISTERED: 'MEMBER_REGISTERED',
    EMAIL_VERIFICATION_REQUESTED: 'EMAIL_VERIFICATION_REQUESTED',
    PASSWORD_RESET_REQUESTED: 'PASSWORD_RESET_REQUESTED',
    KYC_SUBMITTED: 'KYC_SUBMITTED',
    KYC_APPROVED: 'KYC_APPROVED',
    KYC_REJECTED: 'KYC_REJECTED',
    KYC_CORRECTION_REQUESTED: 'KYC_CORRECTION_REQUESTED',
    LOAN_APPLICATION_SUBMITTED: 'LOAN_APPLICATION_SUBMITTED',
    LOAN_MORE_INFORMATION_REQUIRED: 'LOAN_MORE_INFORMATION_REQUIRED',
    LOAN_APPROVED: 'LOAN_APPROVED',
    LOAN_REJECTED: 'LOAN_REJECTED',
    LOAN_DISBURSED: 'LOAN_DISBURSED',
    LOAN_COMPLETED: 'LOAN_COMPLETED',
    LOAN_DEFAULTED: 'LOAN_DEFAULTED',
    GUARANTOR_REQUESTED: 'GUARANTOR_REQUESTED',
    GUARANTOR_ACCEPTED: 'GUARANTOR_ACCEPTED',
    GUARANTOR_DECLINED: 'GUARANTOR_DECLINED',
    GUARANTOR_RELEASED: 'GUARANTOR_RELEASED',
    GUARANTOR_BORROWER_OVERDUE: 'GUARANTOR_BORROWER_OVERDUE',
    PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
    /** A member says they have paid; an officer has to check it. */
    REPAYMENT_DECLARED: 'REPAYMENT_DECLARED',
    PAYMENT_DUE_SOON: 'PAYMENT_DUE_SOON',
    PAYMENT_DUE_TODAY: 'PAYMENT_DUE_TODAY',
    PAYMENT_OVERDUE: 'PAYMENT_OVERDUE',
};
export const AuditAction = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    STATUS_CHANGE: 'STATUS_CHANGE',
    LOGIN: 'LOGIN',
    LOGIN_FAILED: 'LOGIN_FAILED',
    LOGOUT: 'LOGOUT',
    PASSWORD_CHANGED: 'PASSWORD_CHANGED',
    TWO_FACTOR_ENABLED: 'TWO_FACTOR_ENABLED',
    TWO_FACTOR_DISABLED: 'TWO_FACTOR_DISABLED',
    KYC_APPROVED: 'KYC_APPROVED',
    KYC_REJECTED: 'KYC_REJECTED',
    LOAN_APPROVED: 'LOAN_APPROVED',
    LOAN_REJECTED: 'LOAN_REJECTED',
    LOAN_DISBURSED: 'LOAN_DISBURSED',
    REPAYMENT_RECORDED: 'REPAYMENT_RECORDED',
    REPAYMENT_REVERSED: 'REPAYMENT_REVERSED',
    DOCUMENT_DOWNLOADED: 'DOCUMENT_DOWNLOADED',
    EXPORT_GENERATED: 'EXPORT_GENERATED',
    SETTINGS_CHANGED: 'SETTINGS_CHANGED',
    PERMISSION_CHANGED: 'PERMISSION_CHANGED',
};
export const ConfirmationType = {
    LOAN_APPLICATION: 'LOAN_APPLICATION',
    LOAN_OFFER_ACCEPTANCE: 'LOAN_OFFER_ACCEPTANCE',
    GUARANTOR_CONSENT: 'GUARANTOR_CONSENT',
    KYC_DECLARATION: 'KYC_DECLARATION',
    TERMS_ACCEPTANCE: 'TERMS_ACCEPTANCE',
    PRIVACY_ACCEPTANCE: 'PRIVACY_ACCEPTANCE',
};
export const Gender = {
    MALE: 'MALE',
    FEMALE: 'FEMALE',
};
export const EmploymentStatus = {
    EMPLOYED: 'EMPLOYED',
    SELF_EMPLOYED: 'SELF_EMPLOYED',
    UNEMPLOYED: 'UNEMPLOYED',
    STUDENT: 'STUDENT',
    RETIRED: 'RETIRED',
    OTHER: 'OTHER',
};
export const RepaymentDeclarationStatus = {
    PENDING: 'PENDING',
    CONFIRMED: 'CONFIRMED',
    REJECTED: 'REJECTED',
};
//# sourceMappingURL=enums.js.map